import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTransactionAnalytics, useFinancialFlowAnalytics } from '../../../hooks/useAnalytics';
import { useNormalizedTransactionData } from '../../../hooks/useNormalizedData';
import MetricCard from '../core/MetricCard';
import KPICard from '../core/KPICard';
import AreaChart from '../../ui/AreaChart';
import BarChart from '../../ui/BarChart';
import PieChart from '../../ui/PieChart';
import DataTable from '../../ui/DataTable';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { formatPercent } from '../../../utils/format';
import { useFormatting } from '../../../hooks/useFormatting';
import './TransactionMetrics.css';

/**
 * Transaction Metrics Component
 * Displays transaction-related analytics and statistics with improved design
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const TransactionMetrics = ({ period, compact = false }) => {
  const { data: txData, loading, error } = useTransactionAnalytics(period);
  const { data: financialFlow } = useFinancialFlowAnalytics(period);

  // Normalize data for safe access
  const normalized = useNormalizedTransactionData(txData);

  // Generate chart data for key metrics using real data (must be before any early returns)
  const [transactionCountChart, setTransactionCountChart] = useState(null);
  const [requestVolumeChart, setRequestVolumeChart] = useState(null);

  useEffect(() => {
    if (!txData) {
      setTransactionCountChart(null);
      setRequestVolumeChart(null);
      return;
    }

    const currentCount = txData.totalTransactionCount || 0;
    const growthCount = txData.growth?.totalTransactionCount || 0;
    const previousCount = growthCount !== 0 && currentCount !== 0 
      ? currentCount / (1 + (growthCount / 100))
      : currentCount * 0.9;

    const currentVolume = txData.totalRequestVolume || 0;
    const growthVolume = txData.growth?.totalRequestVolume || 0;
    const previousVolume = growthVolume !== 0 && currentVolume !== 0 
      ? currentVolume / (1 + (growthVolume / 100))
      : currentVolume * 0.9;

    // Try to fetch real data from transactions collection
    Promise.all([
      transformToChartDataAsync(
        { totalTransactionCount: currentCount },
        { totalTransactionCount: previousCount },
        'totalTransactionCount',
        period,
        {
          collectionName: 'transactions',
          valueExtractor: () => 1 // Count transactions
        }
      ),
      transformToChartDataAsync(
        { totalRequestVolume: currentVolume },
        { totalRequestVolume: previousVolume },
        'totalRequestVolume',
        period,
        {
          collectionName: 'paymentRequests',
          valueExtractor: (docData) => parseFloat(docData.amount || docData.value || 0)
        }
      )
    ]).then(([countChart, volumeChart]) => {
      setTransactionCountChart(countChart);
      setRequestVolumeChart(volumeChart);
    }).catch(() => {
      // Fallback to synchronous generated data
      setTransactionCountChart(transformToChartData(
        { totalTransactionCount: currentCount },
        { totalTransactionCount: previousCount },
        'totalTransactionCount',
        period
      ));
      setRequestVolumeChart(transformToChartData(
        { totalRequestVolume: currentVolume },
        { totalRequestVolume: previousVolume },
        'totalRequestVolume',
        period
      ));
    });
  }, [txData, period]);

  // Get formatting functions from hook
  const { formatNumber } = useFormatting();

  // Prepare table data for blockchain networks (must be before any early returns)
  const blockchainTableColumns = [
    { key: 'network', label: 'Network' },
    { 
      key: 'count', 
      label: 'Transaction Count',
      render: (value) => typeof value === 'number' ? formatNumber(value) : value
    },
    { 
      key: 'percentage', 
      label: 'Percentage',
      render: (value) => typeof value === 'number' ? formatPercent(value / 100, 1) : value
    }
  ];

  const blockchainTableData = useMemo(() => {
    if (!normalized.blockchainNetworks || Object.keys(normalized.blockchainNetworks).length === 0) return [];
    const total = Object.values(normalized.blockchainNetworks).reduce((sum, count) => sum + (typeof count === 'number' ? count : 0), 0);
    return Object.entries(normalized.blockchainNetworks).map(([network, count]) => ({
      id: network,
      network: network.charAt(0).toUpperCase() + network.slice(1),
      count: typeof count === 'number' ? count : 0,
      percentage: total > 0 ? ((count / total) * 100) : 0
    })).sort((a, b) => b.count - a.count);
  }, [normalized.blockchainNetworks]);

  // Memoize chart data
  const requestSendChartData = useMemo(() => {
    if (!normalized.totalRequestCount && !normalized.totalSendCount) return null;
    return [
      { label: 'Requests', value: normalized.totalRequestCount },
      { label: 'Sends', value: normalized.totalSendCount }
    ].filter(item => item.value > 0);
  }, [normalized.totalRequestCount, normalized.totalSendCount]);

  const blockchainChartData = useMemo(() => {
    if (!blockchainTableData || blockchainTableData.length === 0) return null;
    return blockchainTableData.map(item => ({
      label: item.network,
      value: item.count
    }));
  }, [blockchainTableData]);

  // Error state
  if (error) {
    return (
      <div className="transaction-metrics error">
        <h3>Error loading transaction metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  return (
    <div className="transaction-metrics-enhanced">
      {/* Hero Section - Key KPIs */}
      <section className="transaction-hero">
        <div className="transaction-hero-header">
          <h1 className="transaction-title">
            <i className="bi bi-cash-coin icon-brand"></i>
            Transaction Analytics
          </h1>
          <p className="transaction-subtitle">{period} period</p>
        </div>
        
        <div className="transaction-kpi-grid">
          <KPICard
            title="Total Transaction Value"
            value={normalized.totalTransactionValue || 0}
            formatType="currency"
            icon="bi-cash-stack"
            color="green"
            subtitle="All transactions"
            loading={loading}
          />
          <KPICard
              title="Total Transactions"
            value={normalized.totalTransactionCount || 0}
              formatType="number"
            icon="bi-list-check"
              color="blue"
              subtitle={`${period} period`}
            loading={loading}
            />
          <KPICard
            title="Average Transaction"
            value={normalized.averageTransactionValue || 0}
              formatType="currency"
            icon="bi-bar-chart"
            color="purple"
            subtitle="Per transaction"
              loading={loading}
          />
        </div>
      </section>

      {/* Financial Flow Breakdown - Prominent Section */}
      <section className="transaction-section financial-flow-section">
        <div className="section-header">
          <h2 className="section-title">
            <i className="bi bi-diagram-3 icon-brand"></i>
            Financial Flow Breakdown
          </h2>
        </div>

        <div className="financial-flow-grid">
          {/* Splits Card */}
          <div className="flow-card splits-card">
            <div className="flow-card-header">
              <i className="bi bi-scissors flow-icon"></i>
              <h3>Splits (1:1 Transfers)</h3>
            </div>
            <div className="flow-card-content">
              <div className="flow-stat">
                <span className="flow-stat-label">Total Splits</span>
                <span className="flow-stat-value">{formatNumber(financialFlow?.splits?.count || 0)}</span>
              </div>
              <div className="flow-stat">
                <span className="flow-stat-label">Total Value</span>
                <span className="flow-stat-value currency">{normalized.totalTransactionValue ? `$${formatNumber(normalized.totalTransactionValue)}` : '$0'}</span>
              </div>
              <div className="flow-stats-grid">
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Completed</span>
                  <span className="flow-stat-mini-value">{formatNumber(financialFlow?.splits?.completed?.count || 0)}</span>
                </div>
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Pending</span>
                  <span className="flow-stat-mini-value">{formatNumber(financialFlow?.splits?.pending?.count || 0)}</span>
                </div>
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Active</span>
                  <span className="flow-stat-mini-value">{formatNumber(financialFlow?.splits?.active?.count || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Requests Card */}
          <div className="flow-card requests-card">
            <div className="flow-card-header">
              <i className="bi bi-card-checklist flow-icon"></i>
              <h3>Payment & Withdrawal Requests</h3>
            </div>
            <div className="flow-card-content">
              <div className="flow-stat">
                <span className="flow-stat-label">Total Requests</span>
                <span className="flow-stat-value">{formatNumber(financialFlow?.requests?.count || 0)}</span>
              </div>
              <div className="flow-stat">
                <span className="flow-stat-label">Total Value</span>
                <span className="flow-stat-value currency">{financialFlow?.requests?.totalValue ? `$${formatNumber(financialFlow.requests.totalValue)}` : '$0'}</span>
              </div>
              <div className="flow-stats-grid">
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Payment</span>
                  <span className="flow-stat-mini-value">{formatNumber(financialFlow?.requests?.paymentRequests || 0)}</span>
                </div>
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Withdrawal</span>
                  <span className="flow-stat-mini-value">{formatNumber(financialFlow?.requests?.withdrawalRequests || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shared Wallets Card */}
          <div className="flow-card wallets-card">
            <div className="flow-card-header">
              <i className="bi bi-wallet2 flow-icon"></i>
              <h3>Shared Wallets</h3>
            </div>
            <div className="flow-card-content">
              <div className="flow-stat">
                <span className="flow-stat-label">Total Wallets</span>
                <span className="flow-stat-value">{formatNumber(financialFlow?.sharedWallets?.count || 0)}</span>
              </div>
              <div className="flow-stat">
                <span className="flow-stat-label">Total Balance</span>
                <span className="flow-stat-value currency">{financialFlow?.sharedWallets?.totalBalance ? `$${formatNumber(financialFlow.sharedWallets.totalBalance)}` : '$0'}</span>
              </div>
              <div className="flow-stats-grid">
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Funding</span>
                  <span className="flow-stat-mini-value">{financialFlow?.sharedWallets?.totalFunding ? `$${formatNumber(financialFlow.sharedWallets.totalFunding)}` : '$0'}</span>
                </div>
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Withdrawals</span>
                  <span className="flow-stat-mini-value">{financialFlow?.sharedWallets?.totalWithdrawals ? `$${formatNumber(financialFlow.sharedWallets.totalWithdrawals)}` : '$0'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* User Wallets Card */}
          <div className="flow-card user-wallets-card">
            <div className="flow-card-header">
              <i className="bi bi-person-wallet flow-icon"></i>
              <h3>User Wallets (In-App)</h3>
            </div>
            <div className="flow-card-content">
              <div className="flow-stat">
                <span className="flow-stat-label">Net Flow</span>
                <span className={`flow-stat-value currency ${(financialFlow?.userWallets?.netFlow || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {financialFlow?.userWallets?.netFlow ? `$${formatNumber(financialFlow.userWallets.netFlow)}` : '$0'}
                </span>
              </div>
              <div className="flow-stats-grid">
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Deposits</span>
                  <span className="flow-stat-mini-value">{financialFlow?.userWallets?.totalDeposits ? `$${formatNumber(financialFlow.userWallets.totalDeposits)}` : '$0'}</span>
                </div>
                <div className="flow-stat-mini">
                  <span className="flow-stat-mini-label">Withdrawals</span>
                  <span className="flow-stat-mini-value">{financialFlow?.userWallets?.totalWithdrawals ? `$${formatNumber(financialFlow.userWallets.totalWithdrawals)}` : '$0'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Charts Section */}
      {transactionCountChart && (
        <section className="transaction-section charts-section">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-graph-up icon-brand"></i>
              Transaction Trends
            </h2>
          </div>
          
          <div className="charts-grid">
            {transactionCountChart && (
              <div className="chart-card">
                <h3 className="chart-title">Transaction Count Over Time</h3>
                <AreaChart
                  data={transactionCountChart.data}
                  comparisonData={transactionCountChart.comparisonData}
                  label="Transaction Count"
                  formatType="number"
                  color="blue"
                />
              </div>
            )}
            
            {requestVolumeChart && (
              <div className="chart-card">
                <h3 className="chart-title">Request Volume Over Time</h3>
                <AreaChart
                  data={requestVolumeChart.data}
                  comparisonData={requestVolumeChart.comparisonData}
                  label="Request Volume"
                  formatType="currency"
              color="green"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Additional Metrics - Collapsible */}
      <CollapsibleSection title="Additional Transaction Metrics" defaultExpanded={false}>
        <div className="metrics-grid-wrapper">
          <div className="metrics-grid">
            <div className="metric-group">
              <h3 className="group-title">Request & Send Metrics</h3>
              <div className="metric-cards">
            <MetricCard
              title="Total Requests"
              value={normalized.totalRequestCount}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-send icon-brand"></i>}
              color="orange"
              subtitle="payment requests"
            />
            <MetricCard
              title="Total Request Volume"
              value={normalized.totalRequestVolume}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-cash-stack icon-brand"></i>}
              color="indigo"
              subtitle="request volume"
            />
            <MetricCard
              title="Total Sends"
              value={normalized.totalSendCount}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-inbox icon-brand"></i>}
              color="teal"
              subtitle="send transactions"
            />
            <MetricCard
              title="Total Send Volume"
              value={normalized.totalSendVolume}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-arrow-right-circle icon-brand"></i>}
              color="pink"
              subtitle="send volume"
            />
          </div>
        </div>

        <div className="metric-group">
              <h3 className="group-title">Financial Details</h3>
          <div className="metric-cards">
            <MetricCard
              title="MoonPay Volume"
              value={normalized.moonpayVolume}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-moon icon-brand"></i>}
              color="purple"
              subtitle="MoonPay transactions"
            />
            <MetricCard
              title="Payment Requests"
              value={normalized.paymentRequestsCount}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-card-checklist icon-brand"></i>}
              color="orange"
              subtitle="total requests"
            />
            <MetricCard
              title="Withdrawal Requests"
              value={normalized.withdrawalRequestsCount}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-arrow-up-circle icon-brand"></i>}
              color="red"
              subtitle="withdrawal requests"
            />
            <MetricCard
              title="Total Deposit Value"
              value={normalized.totalDepositValue}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-arrow-down-circle icon-brand"></i>}
              color="green"
              subtitle="total deposits"
            />
            <MetricCard
              title="Total Withdrawal Value"
              value={normalized.totalWithdrawalValue}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-arrow-up-circle icon-brand"></i>}
              color="orange"
              subtitle="total withdrawals"
            />
            <MetricCard
              title="Total Gas Fees"
              value={normalized.totalGasFees}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-lightning icon-brand"></i>}
              color="yellow"
              subtitle="gas fees paid"
            />
          </div>
        </div>
        </div>
      </div>

        {/* Charts in collapsible section */}
        <div className="charts-section-collapsible">
          {requestSendChartData && (
            <div className="chart-card">
              <h3 className="chart-title">Requests vs Sends</h3>
              <BarChart
                data={requestSendChartData}
                label="Requests vs Sends"
                formatType="number"
                orientation="vertical"
          />
        </div>
      )}

          {blockchainChartData && (
            <div className="chart-card">
              <h3 className="chart-title">Blockchain Networks Distribution</h3>
                <PieChart
                  data={blockchainChartData}
                label="Blockchain Networks"
                  formatType="number"
                  showPercentages={true}
                  innerRadius={0.5}
                />
              </div>
          )}
              </div>

        {/* Blockchain Networks Table */}
      {blockchainTableData.length > 0 && (
          <div className="table-section">
            <h3 className="table-title">Blockchain Networks Distribution</h3>
        <DataTable
          data={blockchainTableData}
          columns={blockchainTableColumns}
          loading={loading}
          searchable={true}
          sortable={true}
        />
          </div>
      )}
      </CollapsibleSection>
    </div>
  );
};

TransactionMetrics.propTypes = {
  period: PropTypes.string.isRequired,
  compact: PropTypes.bool
};

export default TransactionMetrics;
